import { useContext, useEffect, useRef } from "react";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { AppContexts } from "../../providers";
import { FEATURES, getFeature } from "./catalog";
import { getUsage, useFeatureUsage } from "./usage";
import { FeatureAction, SubscriptionRequirement } from "./shared";
import { CATEGORIES, readFilters, matchesFeature } from "./filters";
import "./features.css";

const FeatureUsage = ({ feature, metrics, loading }) => {
  const usage = getUsage(feature, metrics);
  return (
    <div className={`rbr-feature-usage is-${usage.state}`}>
      <strong>{!loading && usage.state === "active" && <span aria-hidden="true">✓ </span>}{loading && feature.metric ? "Checking activity…" : usage.label}</strong>
      <span>{loading && feature.metric ? (feature.usageBasis === "current" ? `Counting your ${feature.metricLabel}.` : "Looking for recent team activity.") : usage.detail}</span>
      {feature.scope === "personal" && <span>Personal feature — configured separately for each user.</span>}
    </div>
  );
};

const FeatureDiscovery = () => {
  const { user: account } = useContext(AppContexts.AuthContext);
  const { featureId } = useParams();
  const selected = getFeature(featureId);
  const history = useHistory();
  const location = useLocation();
  const headingRef = useRef(null);
  const { query, view, category } = readFilters(location.search);
  const updateFilters = (changes) => {
    const params = new URLSearchParams(location.search);
    params.delete("requirement");
    Object.entries(changes).forEach(([key, value]) => {
      if (!value || (key !== "q" && value === "all")) params.delete(key);
      else params.set(key, value);
    });
    history.replace({ ...location, search: params.toString() ? `?${params}` : "" });
  };
  const featureUrl = (id) => `/features/${id}${location.search}`;
  const { metrics, loading, failed, retry } = useFeatureUsage(account?.account_id);

  useEffect(() => {
    document.title = `${selected?.title || "Your Features"} - RueBaRue`;
  }, [selected]);

  useEffect(() => {
    if (featureId) headingRef.current?.focus();
  }, [featureId]);

  const visible = FEATURES.filter((feature) => matchesFeature(
    feature, { query, view, category }, metrics,
  ));
  const showLoading = loading && view !== "all";
  const activityError = failed && (
    <div className="rbr-discovery-error" role="status">
      Some activity could not be loaded. Your features and instructions are still available.{" "}
      <button type="button" onClick={retry}>Retry activity</button>
    </div>
  );

  return (
    <section className="rbr-features-page">
      <div id="hms-page-title"><h1 className="uk-heading-small" ref={headingRef} tabIndex={-1}>{selected?.title || "Your Features"}</h1></div>
      <div id="hms-main-body">
        {!featureId && <div className="rbr-discovery-intro">
          <div>
            <p>Discover what your team can do, find instructions, and take the next step.</p>
          </div>
          <Link className="uk-button hms-btn hms-white-btn uk-flex-inline uk-flex-middle" to="/whats-new">See what’s new →</Link>
        </div>}
        {featureId ? (
          <>
            <Link className="rbr-feature-back" to={`/features${location.search}`}>← Back to features</Link>
            {selected ? (
              <article className="rbr-feature-card rbr-feature-detail">
                <span className="rbr-discovery-eyebrow">{selected.category}</span>
                <p className="rbr-feature-benefit">{selected.description}</p>
                <SubscriptionRequirement feature={selected} />
                <FeatureUsage feature={selected} metrics={metrics} loading={loading} />
                {activityError}
                <h2>How to use {selected.title}</h2>
                <ol className="rbr-feature-steps">{selected.steps.map((step) => <li key={step}>{step}</li>)}</ol>
                <div className="hms-guest-btn">
                  <FeatureAction feature={selected} account={account} />
                  {selected.learnMoreUrl && <a className="uk-button hms-btn hms-white-btn uk-flex-inline uk-flex-middle" href={selected.learnMoreUrl} target="_blank" rel="noopener noreferrer">Learn more ↗</a>}
                  {selected.helpUrl && <a className="uk-button hms-btn hms-white-btn uk-flex-inline uk-flex-middle" href={selected.helpUrl} target="_blank" rel="noopener noreferrer">Read the full guide ↗</a>}
                </div>
                {selected.requirement === "confirm" && <p><a href="mailto:support@ruebarue.com">Contact support</a> to confirm the subscription required for this feature.</p>}
              </article>
            ) : <p role="status">This feature was not found. Browse all features to find its instructions.</p>}
          </>
        ) : (
          <>
            <div className="rbr-feature-filters">
              <label>Search features<input type="search" className="uk-input" placeholder="Search by feature or benefit" value={query} onChange={(event) => updateFilters({ q: event.target.value })} /></label>
              <label>Category<select className="uk-select" value={category} onChange={(event) => updateFilters({ category: event.target.value })}><option value="all">All categories</option>{CATEGORIES.map((name) => <option key={name} value={name}>{name}</option>)}</select></label>
            </div>
            <div className="rbr-feature-views" role="group" aria-label="Feature views">
              {[ ["all", "All features"], ["active", "Features you use"], ["explore", "Features to explore"] ].map(([value, label]) => (
                <button key={value} type="button" aria-pressed={view === value} onClick={() => updateFilters({ view: value })}>{label}</button>
              ))}
            </div>
            {activityError}
            <p className="rbr-discovery-muted" role="status">{loading ? "Checking recent activity…" : `${visible.length} ${visible.length === 1 ? "feature" : "features"}`}</p>
            <div className="rbr-feature-grid">
              {!showLoading && visible.map((feature) => (
                <article key={feature.id} className="rbr-feature-card">
                  <span className="rbr-discovery-eyebrow">{feature.category}</span>
                  <h2><Link to={featureUrl(feature.id)}>{feature.title}</Link></h2>
                  <p className="rbr-feature-benefit">{feature.description}</p>
                  <SubscriptionRequirement feature={feature} />
                  <FeatureUsage feature={feature} metrics={metrics} loading={loading} />
                  <div className="hms-guest-btn">
                    <FeatureAction feature={feature} account={account} />
                    <Link className="uk-button hms-btn hms-white-btn uk-flex-inline uk-flex-middle" to={featureUrl(feature.id)}>How to use</Link>
                  </div>
                </article>
              ))}
            </div>
            {!showLoading && !visible.length && <div className="rbr-feature-empty"><h2>{view === "active" ? "No matching features with recorded activity" : "No matching features"}</h2><p>{view === "active" ? "Your team may use features we do not track yet. Browse all features for instructions." : "Try a different search or category."}</p><button type="button" className="uk-button hms-btn hms-white-btn" onClick={() => updateFilters({ q: "", category: "all", view: "all" })}>Show all features</button></div>}
            <p className="secondary uk-margin-small-top">Usage reflects your team’s recorded activity in the last 12 months. Area Guide and Extras use the current number of items in your account. Text Automation uses active scheduled messages. Features to explore also includes features whose usage is not tracked yet.</p>
          </>
        )}
      </div>
    </section>
  );
};

export default FeatureDiscovery;
